import React, { useState, useRef, useEffect } from 'react';
import { commandMap } from '../commands';
import connect from '../commands/connect';
import disconnect from '../commands/disconnect';
import miningCommands from '../commands/mining';
import clear from '../commands/clear';
import balance from '../commands/balance';
import restore from '../commands/restore';
import { getAIResponse } from '../utils/AIService';

const Terminal: React.FC = () => {
  const [input, setInput] = useState('');
  const [isMining, setIsMining] = useState(false);
  const [history, setHistory] = useState<string[]>([
    'CRYPTO AI v1.0.1',
    'Welcome to the most advanced AI-powered Crypto Assistant.',
    "Type 'help' for a list of available commands.",
    'Mine, snipe or stake cryptocurrencies across Solana & EVM chains to earn rewards.',
  ]);
  const inputRef = useRef<HTMLInputElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  const [connectedNetwork, setConnectedNetwork] = useState<
    'solana' | 'ethereum' | 'base' | 'arbitrum' | 'bnb' | 'optimism' | null
  >(null);

  const [fontSize, setFontSize] = useState(16); // Default font size

  const scrollToBottom = () => {
    historyRef.current?.scrollTo(0, historyRef.current.scrollHeight);
  };

  useEffect(() => {
    scrollToBottom();
  }, [history]);

  const runCommand = async (cmd: string) => {
    const lower = cmd.toLowerCase();

    if (lower.startsWith('connect')) {
      const network = cmd.split(' ')[1]?.toLowerCase();
      if (connectedNetwork) {
        setHistory((prev) => [...prev, `⚠ Already connected to ${connectedNetwork}.`]);
        return;
      }
      const validNetworks = ['solana', 'ethereum', 'base', 'arbitrum', 'bnb', 'optimism'];
      if (validNetworks.includes(network)) {
        const result = await connect(network as any, setConnectedNetwork);
        if (result[0].includes('successfully')) {
          setConnectedNetwork(network as any);
        }
        setHistory((prev) => [...prev, `> ${cmd}`, ...result]);
      } else {
        setHistory((prev) => [...prev, `> ${cmd}`, `⚠ Unsupported network.`]);
      }
    } else if (lower === 'disconnect') {
      if (!connectedNetwork) {
        setHistory((prev) => [...prev, `> ${cmd}`, '⚠ No wallet is connected.']);
        return;
      }
      // Check if mining is active and stop it before disconnecting
      if (isMining) {
        await miningCommands('stop', setHistory, connectedNetwork);
        setIsMining(false); // Update the state to indicate mining has stopped
      }
      const result = await disconnect(connectedNetwork, setHistory, setConnectedNetwork);
      setHistory((prev) => [...prev, `> ${cmd}`, ...result]);
    } else if (lower === 'balance') {
      const result = await balance(connectedNetwork);
      setHistory((prev) => [...prev, `> ${cmd}`, ...result]);
    } else if (lower.startsWith('mining')) {
      const miningCommand = cmd.split(' ')[1]?.toLowerCase();
      if (!miningCommand) {
        setHistory((prev) => [...prev, `> ${cmd}`, '⚠ Unknown mining command. Try "mining start", "mining stop", "mining profit", or "mining reset".']);
        return;
      }
      if (!connectedNetwork) {
        setHistory((prev) => [...prev, `> ${cmd}`, '⚠ No wallet connected.']);
        return;
      }
      if (miningCommand === 'start') {
        // If mining starts, set the mining status to true
        setIsMining(true);
      } else if (miningCommand === 'stop') {
        // If mining stops, set the mining status to false
        setIsMining(false);
      }
      const miningResult = await miningCommands(miningCommand, setHistory, connectedNetwork);
      setHistory((prev) => [...prev, `> ${cmd}`, ...miningResult]);
    } else if (lower === 'clear') {
      clear('', setHistory);
      return;
    } else if (lower.startsWith('restore')) {
      const privateKey = cmd.replace('restore ', '').trim();
      const messages = await restore(privateKey, setHistory);
      setHistory((prev) => [...prev, ...messages]);
      return;
    } else if (commandMap[lower]) {
      const result = await commandMap[lower](cmd, setHistory, setConnectedNetwork, connectedNetwork);
      setHistory((prev) => [...prev, `> ${cmd}`, ...result]);
    } else {
      setHistory((prev) => [...prev, `> ${cmd}`, '⌛ Processing...']);
      const aiRawResponse = await getAIResponse(cmd);
      let aiData: any = {};
      try {
        aiData = JSON.parse(aiRawResponse || '{}');
      } catch {
        aiData = { message: aiRawResponse };
      }
      const message =
        aiData.message ||
        '🤖 Assistant is currently unavailable. Type "help" for available commands.';
      setHistory((prev) => [...prev.slice(0, -1), message]);
      if (aiData.command && typeof aiData.command === 'string') {
        await runCommand(aiData.command);
      }
    }
  };

  const handleCommand = async (cmd: string) => {
    await runCommand(cmd);
  };

  const onEnter = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const trimmed = input.trim();
      setInput('');
      if (trimmed) await handleCommand(trimmed);
    }
  };

  // Functions to increase and decrease font size
  const increaseFontSize = () => {
    if (fontSize < 24) setFontSize(fontSize + 2); // Limit the max size
  };

  const decreaseFontSize = () => {
    if (fontSize > 12) setFontSize(fontSize - 2); // Limit the min size
  };

  return (
    <div className="w-full max-w-5xl bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#0f0f0f] rounded-2xl shadow-2xl border border-cyan-600 p-4 sm:p-6">
      <div className="flex justify-between items-center">
        <div className="text-center text-lg sm:text-xl font-mono text-cyan-400 py-2 sm:py-4 border-b border-cyan-700 uppercase tracking-widest glow">
          Terminal
        </div>
        {/* Font size control buttons */}
        <div className="flex space-x-2 items-center text-cyan-400">
          <button
            onClick={decreaseFontSize}
            className="px-3 py-1.5 bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#0f0f0f] 
               border border-cyan-600 font-semibold rounded-md 
               transition duration-300"
          >
            -
          </button>
          <button
            onClick={increaseFontSize}
            className="px-3 py-1.5 bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#0f0f0f] 
               border border-cyan-600 font-semibold rounded-md 
               transition duration-300"
          >
            +
          </button>
        </div>
      </div>
      <div
        className="p-3 sm:p-4 h-[65vh] sm:h-[70vh] flex flex-col rounded-b-2xl overflow-hidden"
        onClick={() => inputRef.current?.focus()}
      >
        <div
          className="flex-1 overflow-y-auto space-y-2 text-base sm:text-lg font-mono scrollbar-thin scrollbar-thumb-cyan-500 scrollbar-track-[#0a0a0a] pr-2"
          ref={historyRef}
          style={{ fontSize: `${fontSize}px` }} // Dynamically update font size
        >
          {history.map((line, i) => {
            let lineClass = 'text-cyan-300 hover:text-cyan-200'; // default system
            if (line.startsWith('> ')) {
              lineClass = 'text-white hover:text-gray-300 font-semibold'; // user input
            } else if (line.startsWith('⚠') || line.startsWith('⌛')) {
              lineClass = 'text-yellow-400 hover:text-yellow-300 animate-pulse'; // warnings
            } else if (line.startsWith('🤖')) {
              lineClass = 'text-fuchsia-400 hover:text-fuchsia-300'; // AI response
            }
            else if (line.includes('success')) {
              lineClass = 'text-green-400 hover:text-green-300'; // AI response
            }
            else if (line.includes('tx:')) {
              // Example: "tx: <hash> <amount> <other info>"
              const parts = line.split(' ');
              const txHash = parts[1]; // Assuming the second part is the transaction hash
              const amount = parts[3]; // Assuming the third part is the amount
              const token = parts[4]; // Assuming the third part is the block number
              const otherInfo = parts.slice(5).join(' '); // The rest could be other transaction info

              return (
                <div>
                  <span className="text-blue-400">tx: </span>
                  <span className="text-green-400">{txHash} | </span>
                  <span className="text-purple-400">{amount} </span>
                  <span className="text-purple-400">{token}</span>
                  <span className="text-gray-400"> {otherInfo}</span>
                </div>
              );
            }
            else if (line.startsWith('Total mined:')) {
              return (
                <div className="text-yellow-500 font-semibold">
                  {line}
                </div>
              );
            }
            else if (line.includes('Mining stopped. Final earnings:')) {
              return (
                <div className="text-pink-500 text-xl font-semibold animate-pulse">
                  {line}
                </div>
              );
            }
            return (
              <div
                key={i}
                className={`break-words transition-all duration-300 ${lineClass}`}
              >
                {line}
              </div>
            );
          })}
        </div>
        <div className="flex items-center mt-3 sm:mt-4 bg-[#0f0f0f] rounded-lg border border-cyan-600 px-3 py-3 shadow-inner focus-within:ring-2 focus-within:ring-cyan-500 relative">
          <span className="text-pink-500 font-bold mr-2 animate-pulse">&gt;</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onEnter}
            autoFocus
            className="flex-1 bg-transparent outline-none text-gray-100 placeholder-gray-500 text-base sm:text-lg"
            placeholder="Type a command..."
          />
          <span className="absolute right-4 w-2 h-2 bg-cyan-400 rounded-full animate-ping" />
        </div>
      </div>
    </div>
  );
};

export default Terminal;
